import React, { useEffect, useState, useCallback } from 'react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { Shield, Save, RefreshCw, Infinity, AlertTriangle } from 'lucide-react';
import { getAdminQuotas, setAdminQuota, ApiCallQuotaRow } from '../../services/api';

const TIERS = ['trial', 'growth', 'pro', 'enterprise'] as const;
const TIER_LABELS: Record<string, string> = {
    trial: 'Trial', growth: 'Growth', pro: 'Pro', enterprise: 'Enterprise',
};

const getApiError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
    return fallback;
};

interface EditState {
    [key: string]: { value: string; unlimited: boolean };
}

const ApiQuotaAdmin: React.FC = () => {
    const [rows, setRows] = useState<ApiCallQuotaRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<string | null>(null);
    const [edits, setEdits] = useState<EditState>({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getAdminQuotas();
            setRows(data);
            // Initialise edit state from loaded values
            const init: EditState = {};
            data.forEach(r => {
                const k = `${r.surface}:${r.tier}`;
                init[k] = {
                    value: r.daily_limit === -1 ? '' : String(r.daily_limit),
                    unlimited: r.daily_limit === -1,
                };
            });
            setEdits(init);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to load quota settings.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSave = async (surface: string, tier: string) => {
        const k = `${surface}:${tier}`;
        const edit = edits[k];
        if (!edit) return;
        const limit = edit.unlimited ? -1 : parseInt(edit.value, 10);
        if (!edit.unlimited && (isNaN(limit) || limit < 0)) {
            setError('Enter a valid positive number or toggle Unlimited.');
            return;
        }
        setSaving(k);
        setError('');
        setSuccess('');
        try {
            await setAdminQuota(surface, tier, limit);
            setSuccess(`${surface} / ${tier} updated to ${limit === -1 ? 'Unlimited' : limit + '/day'}`);
            await load();
        } catch (e: any) {
            setError(getApiError(e, 'Save failed.'));
        } finally {
            setSaving(null);
        }
    };

    // Group rows by surface
    const surfaces = Array.from(new Set(rows.map(r => r.surface)));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
                        API Call Quotas
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Daily call limits per subscription tier. Changes take effect within 5 minutes (cache TTL).
                    </p>
                </div>
                <GlassButton variant="secondary" onClick={load} disabled={loading}>
                    <RefreshCw size={14} /> Refresh
                </GlassButton>
            </div>

            {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, color: 'var(--danger)', fontSize: '0.875rem' }}>
                    <AlertTriangle size={16} />{error}
                </div>
            )}
            {success && (
                <div style={{ padding: '12px 16px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 8, color: 'var(--success)', fontSize: '0.875rem' }}>
                    ✓ {success}
                </div>
            )}

            {loading ? (
                <GlassCard><p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: 32 }}>Loading quotas...</p></GlassCard>
            ) : (
                surfaces.map(surface => {
                    const surfaceRows = rows.filter(r => r.surface === surface);
                    const label = surfaceRows[0]?.surface_label ?? surface;
                    const todayTotal = surfaceRows[0]?.total_calls_today ?? 0;

                    return (
                        <GlassCard key={surface}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--primary-500)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Shield size={18} color="#fff" />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, color: 'var(--text)' }}>{label}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>surface: <code>{surface}</code></div>
                                    </div>
                                </div>
                                <div style={{ padding: '4px 12px', background: 'var(--panel-strong)', borderRadius: 20, border: '1px solid var(--stroke)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                    {todayTotal.toLocaleString()} calls today (all users)
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                {TIERS.map(tier => {
                                    const row = surfaceRows.find(r => r.tier === tier);
                                    if (!row) return null;
                                    const k = `${surface}:${tier}`;
                                    const edit = edits[k] ?? { value: '', unlimited: false };
                                    const isDirty = edit.unlimited
                                        ? row.daily_limit !== -1
                                        : parseInt(edit.value, 10) !== row.daily_limit;

                                    return (
                                        <div key={tier} style={{ padding: 16, background: 'var(--panel-strong)', borderRadius: 10, border: `1px solid ${isDirty ? 'var(--primary-500)' : 'var(--stroke)'}`, transition: 'border-color 0.15s' }}>
                                            <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: 10 }}>
                                                {TIER_LABELS[tier]}
                                            </div>

                                            {/* Unlimited toggle */}
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={edit.unlimited}
                                                    onChange={e => setEdits(prev => ({ ...prev, [k]: { ...edit, unlimited: e.target.checked } }))}
                                                    style={{ accentColor: 'var(--primary-500)' }}
                                                />
                                                <Infinity size={13} /> Unlimited
                                            </label>

                                            {/* Value input */}
                                            {!edit.unlimited && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={edit.value}
                                                        onChange={e => setEdits(prev => ({ ...prev, [k]: { ...edit, value: e.target.value } }))}
                                                        style={{
                                                            flex: 1, padding: '8px 10px', borderRadius: 6,
                                                            border: '1px solid var(--stroke)', background: 'var(--panel)',
                                                            color: 'var(--text)', fontSize: '0.9rem', fontWeight: 600,
                                                            outline: 'none',
                                                        }}
                                                        placeholder="calls/day"
                                                    />
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>/day</span>
                                                </div>
                                            )}

                                            <GlassButton
                                                variant={isDirty ? 'primary' : 'secondary'}
                                                onClick={() => handleSave(surface, tier)}
                                                disabled={saving === k}
                                                style={{ width: '100%', justifyContent: 'center', fontSize: '0.8rem', padding: '8px 12px' }}
                                            >
                                                <Save size={13} />
                                                {saving === k ? 'Saving...' : isDirty ? 'Save' : 'Saved'}
                                            </GlassButton>
                                        </div>
                                    );
                                })}
                            </div>
                        </GlassCard>
                    );
                })
            )}
        </div>
    );
};

export default ApiQuotaAdmin;
